/**
 * useAnalyticsData — TanStack Query hooks for analytics data fetching.
 */
import { useQuery } from '@tanstack/react-query';

const API_BASE = process.env.REACT_APP_API_BASE || '';

export interface QueueTimePoint {
  timestamp: string;
  waitTimeMinutes: number;
}

export interface LabelDistribution {
  walkOn: number;
  shortWait: number;
  moderateWait: number;
  longWait: number;
}

export function useQueueTimeSeries(
  attractionId: string,
  start?: string,
  end?: string,
) {
  return useQuery<QueueTimePoint[]>({
    queryKey: ['admin', 'analytics', 'queue-times', attractionId, start, end],
    queryFn: async () => {
      let url = `${API_BASE}/api/admin/analytics/queue-times?attractionId=${encodeURIComponent(attractionId)}`;
      if (start) url += `&start=${encodeURIComponent(start)}`;
      if (end) url += `&end=${encodeURIComponent(end)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      return response.json() as Promise<QueueTimePoint[]>;
    },
    enabled: !!attractionId,
  });
}

export function useLabelDistribution(
  attractionId: string,
  start?: string,
  end?: string,
) {
  return useQuery<LabelDistribution>({
    queryKey: ['admin', 'analytics', 'distribution', attractionId, start, end],
    queryFn: async () => {
      let url = `${API_BASE}/api/admin/analytics/distribution?attractionId=${encodeURIComponent(attractionId)}`;
      if (start) url += `&start=${encodeURIComponent(start)}`;
      if (end) url += `&end=${encodeURIComponent(end)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      return response.json() as Promise<LabelDistribution>;
    },
    enabled: !!attractionId,
  });
}

export function useThresholds() {
  return useQuery<Record<string, { walkOn: number; shortWait: number; moderateWait: number; longWait: number }>>({
    queryKey: ['admin', 'analytics', 'thresholds'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE}/api/admin/analytics/thresholds`);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      return response.json() as Promise<Record<string, { walkOn: number; shortWait: number; moderateWait: number; longWait: number }>>;
    },
  });
}

/** Multi-attraction overlay: fetches queue time series for multiple attractions in parallel. */
export interface QueueTimeSeriesGroup {
  [attractionId: string]: QueueTimePoint[];
}

export function useQueueTimeSeriesGroup(
  attractionIds: string[],
  start?: string,
  end?: string,
) {
  return useQuery<QueueTimeSeriesGroup>({
    queryKey: ['admin', 'analytics', 'queue-times-group', attractionIds, start, end],
    queryFn: async () => {
      let url = `${API_BASE}/api/admin/analytics/queue-times-group?`;
      attractionIds.forEach((id, idx) => {
        url += `${idx === 0 ? '' : '&'}attractionId=${encodeURIComponent(id)}`;
      });
      if (start) url += `&start=${encodeURIComponent(start)}`;
      if (end) url += `&end=${encodeURIComponent(end)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      return response.json() as Promise<QueueTimeSeriesGroup>;
    },
    enabled: attractionIds.length > 0,
  });
}
